import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';

const PartnerSection = () => {
  const sectionStyles = {
    padding: '2rem 0',
  };

  const titleStyles = {
    color: '#410684', 
    textAlign: 'center',
    fontSize: window.innerWidth < 768 ? '35px' : '65px',
    lineHeight:'72px',
    fontWeight: 700,
    marginBottom: '20px',
    fontFamily: 'Oswald, sans-serif',
  };

  const descriptionStyles = {
    color: 'rgb(65, 64, 66)',
    maxWidth: '910px',
    textAlign: 'center',
    margin: '0 auto 2rem auto',
    padding: '0 1rem',
    lineHeight: '24px',
    fontSize: '1rem',
  };

  return (
    <div style={sectionStyles}>
      <h2 style={titleStyles}>OUR TRUSTED PARTNERS</h2>
      <p style={descriptionStyles}>
        We have a pool of trained professionals & skilled technicians in the field of ICT, 
        and we supply these talents to our clients. Our partnerships with industry leaders 
        enable us to deliver cutting-edge solutions.
      </p>
      <Marquee 
        items={[
          { 
            src: 'ringcentral.svg', 
            alt: 'RingCentral',
          },
          { 
            src: 'NEC.png', 
            alt: 'NEC',
          },
          { 
            src: 'ipfone.png', 
            alt: 'IPFone',
          },
          { 
            src: 'jbs.png', 
            alt: 'JBS',
          },
        ]}
        duration={15}
        containerStyle={{ 
          minHeight: '60px',
          padding: '10px 0',
        }}
      />
    </div>
  );
};

const Marquee = ({ items, duration = 20, containerStyle = {} }) => {
  const [isMounted, setIsMounted] = useState(false);
  const [screenWidth, setScreenWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 0);
  const containerRef = useRef(null);
  const marqueeRef = useRef(null);
  const [multiplier, setMultiplier] = useState(1);

  const calculateWidth = useCallback(() => {
    if (!containerRef.current || !marqueeRef.current) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const marqueeRect = marqueeRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const marqueeWidth = marqueeRect.width;

    if (containerWidth && marqueeWidth) {
      const newMultiplier = Math.max(2, Math.ceil((containerWidth * 2) / marqueeWidth));
      setMultiplier(newMultiplier);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
      calculateWidth();
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [calculateWidth]);

  useEffect(() => {
    if (!isMounted) return;
    calculateWidth();
    
    const resizeObserver = new ResizeObserver(calculateWidth);
    if (marqueeRef.current) resizeObserver.observe(marqueeRef.current);
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    
    return () => resizeObserver.disconnect();
  }, [calculateWidth, isMounted]);

  useEffect(() => {
    setIsMounted(true);
    calculateWidth();
  }, [calculateWidth]);

  const getResponsiveStyles = () => {
    // Mobile styles
    if (screenWidth < 640) {
      return {
        image: {
          height: '30px',
          margin: '0 10px',
        },
        item: {
          margin: '0 15px',
          minWidth: '80px',
        },
        container: {
          minHeight: '50px',
        },
      };
    }
    // Tablet styles
    else if (screenWidth < 1024) {
      return {
        image: {
          height: '40px',
          margin: '0 15px',
        },
        item: {
          margin: '0 20px',
          minWidth: '100px',
        },
        container: {
          minHeight: '60px',
        },
      };
    }
    // Desktop styles
    return {
      image: {
        height: '50px',
        margin: '0 20px',
      },
      item: {
        margin: '0 25px',
        minWidth: '120px',
      },
      container: {
        minHeight: '80px',
      },
    };
  };

  const responsiveStyles = getResponsiveStyles();

  const itemStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.5rem',
    ...responsiveStyles.item,
  };

  const imageStyles = {
    height: responsiveStyles.image.height,
    width: 'auto',
    objectFit: 'contain',
    margin: responsiveStyles.image.margin,
  };

  const containerStyles = {
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'row',
    position: 'relative',
    width: '100%',
    backgroundColor: '#fff',
    padding: '0.5rem 0',
    ...responsiveStyles.container,
    ...containerStyle,
  };

  const marqueeStyles = {
    flexShrink: 0,
    display: 'flex',
    minWidth: 'fit-content',
    alignItems: 'center',
    gap: screenWidth < 640 ? '0.5rem' : '1rem',
  };

  const multiplyChildren = useCallback(
    (count) => {
      return [...Array(count)].map((_, i) => (
        <React.Fragment key={i}>
          {items.map((item, index) => (
            <div key={`${index}-${i}`} style={itemStyles}>
              <img 
                src={item.src} 
                alt={item.alt} 
                style={imageStyles}
              />
            </div>
          ))}
        </React.Fragment>
      ));
    },
    [items, itemStyles, imageStyles]
  );

  const scroll = {
    x: ['0%', '-50%'],
    transition: {
      duration: duration,
      ease: 'linear',
      repeat: Infinity,
    },
  };

  if (!isMounted) return null;

  return (
    <div ref={containerRef} style={containerStyles}>
      <motion.div
        animate={scroll}
        style={{
          ...marqueeStyles,
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <div ref={marqueeRef} style={marqueeStyles}>
          {multiplyChildren(1)}
        </div>
        {multiplyChildren(multiplier - 1)}
      </motion.div>
      <motion.div
        animate={scroll}
        style={{
          ...marqueeStyles,
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        {multiplyChildren(multiplier)}
      </motion.div>
    </div>
  );
};

export default PartnerSection;